import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * EmployeeSearch — fuzzy searchable dropdown for employee selection.
 *
 * Props:
 *   options   – array of { value, label } (label = "FirstName LastName (EmpCode)")
 *   value     – currently selected value (employee id)
 *   onChange  – (name, value) callback matching FormField convention
 *   name      – field name
 *   placeholder – placeholder text
 *   disabled  – disabled state
 *   className – extra CSS class
 */
export default function EmployeeSearch({
  options = [],
  value,
  onChange,
  name,
  placeholder = 'Search employee...',
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState(null); // {left, top, width} for portal dropdown
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  /* Close on outside click */
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* Position the portal dropdown directly under the trigger */
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    function updateCoords() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCoords({ left: r.left, top: r.bottom + 2, width: r.width });
    }
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [open]);

  /* Fuzzy filter */
  const filtered = useMemo(() => {
    if (!search) return options;
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return options.filter(opt => {
      const lbl = opt.label.toLowerCase();
      return terms.every(t => lbl.includes(t));
    });
  }, [options, search]);

  /* Resolve selected label */
  const selectedOpt = options.find(o => o.value === value);
  const displayLabel = selectedOpt ? selectedOpt.label : '';

  function handleSelect(opt) {
    onChange(name, opt.value);
    setSearch('');
    setOpen(false);
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange(name, '');
    setSearch('');
  }

  function handleInputClick() {
    if (disabled) return;
    setOpen(true);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} className={className}>
      {/* Display / trigger */}
      {!open ? (
        <div
          onClick={handleInputClick}
          style={{
            padding: '9px 14px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)',
            color: value ? '#e0e0e0' : '#777',
            fontSize: 14,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 40,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {displayLabel || placeholder}
          </span>
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 8 }}>
            {value && !disabled && (
              <span onClick={handleClear} style={{ cursor: 'pointer', color: '#888', fontSize: 16, lineHeight: 1 }}
                title="Clear">&times;</span>
            )}
            <span style={{ color: '#666', fontSize: 10 }}>&#9660;</span>
          </span>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder}
          autoFocus
          style={{
            width: '100%',
            padding: '9px 14px',
            borderRadius: 6,
            border: '1px solid rgba(0,224,255,0.4)',
            background: 'rgba(255,255,255,0.07)',
            color: '#e0e0e0',
            fontSize: 14,
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') { setOpen(false); setSearch(''); }
            if (e.key === 'Enter' && filtered.length === 1) { handleSelect(filtered[0]); }
          }}
        />
      )}

      {/* Dropdown list — rendered via portal so it escapes parent overflow */}
      {open && coords && createPortal(
        <div
          /* Don't let outside-click close handler see this as "outside" */
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            width: coords.width,
            zIndex: 10000,
            maxHeight: 260,
            overflowY: 'auto',
            background: '#1a1f2e',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}>
          {value && (
            <div
              onClick={() => { onChange(name, ''); setSearch(''); setOpen(false); }}
              style={{
                padding: '9px 14px',
                fontSize: 13,
                color: '#00e0ff',
                cursor: 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,224,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              &#10003; None
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', color: '#666', fontSize: 13 }}>No matches found</div>
          ) : (
            filtered.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt)}
                style={{
                  padding: '9px 14px',
                  fontSize: 13,
                  color: opt.value === value ? '#00e0ff' : '#d0d0d0',
                  cursor: 'pointer',
                  background: opt.value === value ? 'rgba(0,224,255,0.08)' : 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = opt.value === value ? 'rgba(0,224,255,0.08)' : 'transparent'}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
